/* app/seo-linking.jsx - Internal Linking, PROPERTY scoped at BOTH positions,
   wired to GET /api/wp/internal-links-property?site_id=<anchor> plus
   &section_id=<n> when the switcher names a section.

   WHY THIS MODULE HAS NO SECTION-ONLY POSITION: a link from a hand-coded page
   to a blog post crosses sections, so no section can hold the question
   (docs/MODULE-SCOPE.md 3). Read through one connection, as this screen used
   to, it answered a question nobody asked, whether the blog links to itself,
   and every inbound link from the hand-coded marketing pages was invisible, so
   a post linked only from the home page came back as an ORPHAN. That is the
   worst direction for this screen to be wrong in: it tells a customer to fix
   something that is not broken.

   SO THE GRAPH IS ALWAYS THE WHOLE WEBSITE AND ONLY THE LIST NARROWS, and that
   is the one thing a customer with the filter on one section has to be told:
   its orphans were judged against inbound links from everywhere. `graph_why`
   says it in the endpoint's own words and is rendered as it stands.

   AND WHERE THE GRAPH IS PARTIAL, THE ORPHANS ARE CANDIDATES. A section whose
   outgoing links could not be read may be linking to everything, so with
   `sections_unread` non-empty the list is labelled candidate and the sections
   are named. A partial graph's orphan list presented as a finding is the same
   defect in a new place.

   AI link suggestions still aren't built server-side, so per-page "Suggest
   links" honestly returns "no suggestions" where there is a CMS, and a section
   with none offers the handover brief instead, which is the verb page gaps
   already uses for the same situation. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const SeoStat = (p) => React.createElement(window.MlSeoDashboard.SeoStat, p);

  /* The brief, as text, built from the row's own facts and the honesty clause
     that belongs with them. A brief that left out "candidate" would arrive at
     the developer as a certainty the screen itself does not hold. */
  function briefOf(o, unreadNames) {
    const unread = unreadNames || [];
    const lines = [
      `Internal link needed: ${o.title}`,
      o.link ? `URL: ${o.link}` : null,
      `Section: ${o.section_name}`,
      "",
      unread.length
        ? "This page has no inbound internal link in the part of the website that could be read, so it is a CANDIDATE "
          + `orphan rather than a confirmed one. Not readable: ${unread.join(", ")}.`
        : "This page has no inbound internal link anywhere on the website, so a reader or a crawler can only reach it "
          + "by knowing the address.",
      "",
      "The fix: link to it from a page that is itself linked, with anchor text describing what the page is about.",
      o.verb_why || null,
    ];
    return lines.filter(x => x !== null).join("\n");
  }

  function Orphan({ o, atAll, candidate, unreadNames }) {
    const [phase, setPhase] = useState("idle"); // idle | generating | results | error | none
    const [copy, setCopy] = useState(null);     // { ok, msg }
    const { Badge } = window.HuddleDesignSystem_775113;
    const sugg = o.sugg || [];
    const run = () => { setPhase("generating"); setTimeout(() => setPhase(sugg.length ? "results" : "none"), 1600); };
    /* The per row verb. A section with a CMS keeps the affordance it has; one
       without gets the brief, because there is nothing to add a link IN and no
       button can change that. */
    const handover = o.verb === "handover_brief";
    const copyBrief = async () => {
      try {
        if (!navigator.clipboard) throw new Error("This browser will not give the page clipboard access.");
        await navigator.clipboard.writeText(briefOf(o, unreadNames));
        setCopy({ ok: true, msg: `Brief for "${o.title}" copied.` });
      } catch (e) {
        setCopy({ ok: false, msg: e.message || "Could not copy the brief." });
      }
    };
    return (
      <div className="bi-card" style={{ cursor: "default" }}>
        <div className="seo-orphan">
          <div className="seo-orphan__head">
            <div className="seo-orphan__main">
              <span className="seo-orphan__title">{o.title}</span>
              <div className="seo-orphan__sub">
                {/* Every row names its section at All, and the filter removes
                    that and nothing else (MODULE-SCOPE 1.1). */}
                {atAll && <Badge tone={handover ? "warning" : "info"}>{o.section_name}</Badge>}
                <Badge tone="neutral">{o.type === "page" ? "Page" : "Post"}</Badge>
                {candidate && <Badge tone="warning">Candidate</Badge>}
                {o.link
                  ? <a className="bm-post__view" href={o.link} target="_blank" rel="noopener noreferrer">view <I name="external-link" size={11} /></a>
                  : null}
              </div>
            </div>
            {handover
              ? <button className="bi-btn bi-btn--sm" onClick={copyBrief}><I name="copy" size={14} />Copy the handover brief</button>
              : phase === "idle" ? <button className="bi-btn bi-btn--sm bi-btn--ai" onClick={run}><I name="sparkles" size={14} />Suggest links</button>
              : phase === "results" ? <button className="bi-btn bi-btn--sm" onClick={run}><I name="refresh" size={14} />Re-suggest</button>
              : null}
          </div>
          {/* Why the button on this row is the one it is, in the endpoint's
              words, so the answer is the same wherever it is asked. */}
          {handover && o.verb_why && <div className="bm-kw" style={{ marginTop: 8 }}>{o.verb_why}</div>}
          {copy && <div className={"bm-kw" + (copy.ok ? " bm-kw--ok" : " bm-kw--over")} style={{ marginTop: 6 }}>{copy.msg}</div>}
          {phase === "generating" && <div className="seo-sugg-panel"><div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}><span className="ml-spinner ml-spinner--sm ml-spinner--ai" />Finding internal link opportunities…</div></div>}
          {phase === "none" && <div className="seo-sugg-panel"><div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>No strong internal-link suggestions for this page right now.</div></div>}
          {phase === "results" && (
            <div className="seo-sugg-panel">
              {sugg.map(([anchor, target, reason], i) => (
                <div className="seo-sugg" key={i}>
                  <span className="seo-sugg__anchor">{anchor}</span>
                  <I name="arrow-right" size={14} className="seo-sugg__arrow" />
                  <div style={{ flex: 1, minWidth: 0 }}><div className="seo-sugg__target">{target}</div><div className="seo-sugg__reason">{reason}</div></div>
                  <button className="bi-btn bi-btn--sm">Add link</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  function InternalLinking({ state, site, sectionId, sections }) {
    const atAll = sectionId == null;
    const anchorId = site ? site.id : null;
    const [phase, setPhase] = useState("pre"); // pre | running | results | error
    const [res, setRes] = useState(null);
    const [err, setErr] = useState(null);
    // The section filter was refused and the whole connection was read instead.
    // Named on screen, because a widened answer nobody was told about is the
    // same lie as a narrowed one.
    const [widened, setWidened] = useState(null);

    // The request differs per position, so the answer on screen cannot outlive
    // a move of the switcher: it would be labelled with the new filter and
    // built from the old one.
    useEffect(() => { setPhase("pre"); setRes(null); setErr(null); setWidened(null); }, [anchorId, sectionId]);

    const run = useCallback(async () => {
      if (anchorId == null) { setErr("No site is connected yet, so there is no website to map."); setPhase("error"); return; }
      const base = `/wp/internal-links-property?site_id=${anchorId}&perPage=100`;
      setPhase("running"); setWidened(null);
      try {
        const r = await api(atAll ? base : `${base}&section_id=${sectionId}`);
        setRes(r); setErr(null); setPhase("results");
      } catch (e) {
        // A connection with no property row has no sections to filter BY, which
        // the endpoint refuses by name rather than answering emptily. The whole
        // website is still answerable, so it is read and the refusal is shown.
        const code = e && e.data && e.data.code;
        if (!atAll && code === "SECTION_NOT_IN_PROPERTY") {
          try {
            const r = await api(base);
            setRes(r); setErr(null); setWidened(e.data.error || e.message); setPhase("results");
            return;
          } catch { /* the widened read failed too: the error state below */ }
        }
        setErr((e && e.message) || "the link graph could not be built.");
        setPhase("error");
      }
    }, [anchorId, atAll, sectionId]);

    const orphans = ((res && res.orphans) || []).map(o => ({ ...o, sugg: [] }));
    const rows = (res && res.posts) || [];
    const perSection = (res && res.sections) || [];
    // A section is named from the row that carries its name, and `sections` is
    // the only place that does: `sections_unread` rows come back with the key
    // missing, so reading a name off them would print "undefined" into the one
    // sentence that has to name a section to be worth anything.
    const nameOfSection = (id, fallback) => {
      const s = perSection.find(x => x.section_id === id);
      if (s && s.section_name) return s.section_name;
      const p = (sections || []).find(x => x.id === id);
      return (p && p.name) || fallback || `section ${id}`;
    };
    const unread = ((res && res.sections_unread) || [])
      .map(x => ({ ...x, label: nameOfSection(x.section_id, x.name) }));
    // CANDIDATE, not confirmed: a section whose outgoing links could not be
    // read may be linking to everything, so a page listed here may have an
    // inbound link nobody could see.
    const candidate = unread.length > 0;
    const unreadNames = unread.map(x => x.label);
    // The figure and the list are the same thing here (the endpoint caps
    // neither), and one name for it keeps a later cap from being reported twice
    // with two different numbers.
    const orphanCount = (res && res.orphanCount) != null ? res.orphanCount : orphans.length;
    const graphPages = (res && res.graph_pages) || 0;
    const analyzed = (res && res.analyzed) || 0;
    const sectionName = atAll ? null
      : ((perSection.find(s => s.section_id === sectionId) || {}).section_name
         || ((sections || []).find(s => s.id === sectionId) || {}).name
         || "this section");

    const Header = (
      <div className="bi-header">
        <div className="bi-header__titles">
          <h1 className="bi-title"><span className="bi-title__icon"><I name="link" size={20} /></span>Internal Linking</h1>
          <div className="bi-subtitle">
            Link structure · {atAll ? "the whole website" : sectionName + ", judged against the whole website"}
            <span className="seo-free-tag" style={{ marginLeft: 6 }}>Crawl free</span>
          </div>
        </div>
        <div className="bi-header-actions">
          <button className="bi-btn bi-btn--primary" disabled={phase === "running"} onClick={run}>{phase === "running" ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />Analyzing…</> : phase === "results" ? <><I name="refresh" size={16} />Re-analyze</> : <><I name="play" size={16} />Analyze links</>}</button>
        </div>
      </div>
    );
    if (phase !== "results") return (
      <div className="bi-page">{Header}
        <div className="bi-empty-state">
          <span className="bi-empty-state__icon">{phase === "running" ? <span className="ml-spinner ml-spinner--lg" /> : <I name={phase === "error" ? "alert-circle" : "link"} size={30} />}</span>
          <div className="bi-empty-state__title">{phase === "running" ? "Mapping your link structure…" : phase === "error" ? "Couldn't analyze links" : "Analyze your internal links"}</div>
          <p className="bi-empty-state__desc">{phase === "running"
            ? "Reading every section of the website: what each CMS holds, and the pages a section with no CMS serves, which have to be fetched to see the links they carry."
            : phase === "error" ? err
            : "The crawl is free and maps the link graph of the whole website, across every section, flagging pages with no incoming links. A link from a hand-coded page to a blog post counts, which is the question no single section can answer."
              + (atAll ? "" : ` The list will show ${sectionName} only, and the graph stays the whole website.`)}</p>
          {phase !== "running" && <div className="bi-empty-state__actions"><button className="bi-btn bi-btn--primary" onClick={run}><I name="play" size={16} />Analyze links</button></div>}
        </div>
      </div>
    );
    return (
      <div className="bi-page">{Header}
        {widened && (
          <div className="ds-strip ds-strip--error">
            <I name="alert-triangle" size={14} />
            <span>The section filter was not applied: {widened} What follows is this one connection's own pages.</span>
          </div>
        )}
        <div className="seo-stat-row">
          <SeoStat icon="alert-triangle" label={candidate ? "Candidate orphans" : "Orphan pages"} value={String(orphanCount)} />
          <SeoStat icon="link" label="Avg. links / page" value={String((res && res.avgLinks) || 0)} />
          <SeoStat icon="check-circle" label="Well-linked (3+)" value={String((res && res.wellLinked) || 0)} />
          <SeoStat icon="file-text" label="Pages listed" value={String(analyzed)} />
        </div>

        {/* TWO NUMBERS, NEVER ONE WEARING THE OTHER'S LABEL: the graph is the
            whole website, the list is what the filter allows, and at All they
            are still different if a section contributed pages the filter did
            not list. `graph_why` is the endpoint's own sentence. */}
        <div className="bi-hint" style={{ marginBottom: 16 }}>
          <b>The graph is {graphPages} page(s) of the whole website{graphPages === analyzed ? "" : `, and ${analyzed} of them are listed below`}.</b>{" "}
          {res.graph_why}
        </div>

        {/* THE HONESTY CLAUSE, and it decides how the list below may be read.
            `orphans_why` is the endpoint's, and the sections it refers to are
            named here so nobody has to go looking for them. */}
        <div className={candidate ? "ds-strip ds-strip--error" : "ds-strip ds-strip--quiet"}>
          <I name={candidate ? "alert-triangle" : "check-circle"} size={14} />
          <span>
            {res.orphans_why}
            {candidate && <> Not readable: {unread.map(x => `${x.label} (${x.html_read} of ${x.pages} pages read)`).join(", ")}.</>}
          </span>
        </div>

        <p className="seo-intro">
          <b>{orphanCount} {candidate ? "candidate orphan" : "orphan"} page{orphanCount === 1 ? "" : "s"}</b>{" "}
          {orphanCount === 1 ? "has" : "have"}{" "}
          {candidate ? "no incoming internal link in the part of the website that could be read" : "no incoming internal link anywhere on this website"}
          , which makes {orphanCount === 1 ? "it" : "them"} hard for Google and for readers to find.{" "}
          <span style={{ color: "var(--text-disabled)" }}>AI per-page suggestions are coming soon.</span>
        </p>
        {orphanCount === 0
          ? <div className="bi-subempty">No orphan pages: every page listed here has an incoming internal link.</div>
          : <div className="bi-list">{orphans.map(o => <Orphan key={o.key || o.id} o={o} atAll={atAll} candidate={candidate} unreadNames={unreadNames} />)}</div>}

        {/* WHICH SECTIONS THE GRAPH COULD READ, at both positions, because the
            graph is the whole website at both. A section's own row is where
            "capped" and "unreadable" belong: they are facts about the read and
            not about the website. */}
        <div className="bi-results-head"><I name="layers" size={14} />What the graph could read, per section</div>
        <div className="kw-table-wrap" style={{ marginBottom: 18 }}>
          <table className="kw-table">
            <thead><tr><th>Section</th><th>Pages in graph</th><th>Bodies read</th><th>What that means</th></tr></thead>
            <tbody>
              {perSection.map(s => (
                <tr key={s.section_id}>
                  <td>
                    <span className="kw-kw">{s.section_name}</span>
                    {s.can_publish === false ? <span className="seo-prop__type"> no CMS behind it</span> : null}
                  </td>
                  <td className="kw-num">{s.pages}</td>
                  <td className="kw-num">{s.html_read}</td>
                  <td>
                    {s.why || (s.capped
                      ? "Read to the page cap, so this section may serve more pages than the graph holds."
                      : "Every page of this section was read, so its outgoing links are all in the graph.")}
                  </td>
                </tr>
              ))}
              {perSection.length === 0 && <tr><td colSpan={4}>No section of this website could be read into the graph.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* EVERY LISTED PAGE AND ITS LINK COUNTS. Fewest inbound links first,
            because that is the order the work is done in, and a Section column
            at All because a row with no section is a finding nobody can be
            given. The filter removes that column and nothing else. */}
        <div className="bi-results-head"><I name="file-text" size={14} />Every page listed, fewest inbound links first</div>
        <div className="kw-table-wrap">
          <table className="kw-table">
            <thead>
              <tr>
                {atAll && <th>Section</th>}
                <th>Page</th><th>Type</th><th>Inbound</th><th>Outbound</th><th>What can be done</th>
              </tr>
            </thead>
            <tbody>
              {[...rows].sort((a, b) => (a.incoming - b.incoming) || String(a.title).localeCompare(String(b.title))).map(r => (
                <tr key={r.key}>
                  {atAll && <td><span className="kw-kw">{r.section_name}</span></td>}
                  <td>
                    <span className="kw-kw">{r.title}</span>
                    {r.url ? <a className="bm-post__view" style={{ marginLeft: 8 }} href={r.url} target="_blank" rel="noopener noreferrer">view <I name="external-link" size={11} /></a> : null}
                  </td>
                  <td>{r.type === "page" ? "Page" : "Post"}</td>
                  <td className="kw-num">{r.incoming}</td>
                  <td className="kw-num">{r.outgoing}</td>
                  <td title={r.verb_why || undefined}>{r.verb === "handover_brief" ? "Handover brief" : "Add a link in the CMS"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={atAll ? 6 : 5}>No page of this website was listed, so there is nothing to count links on.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  window.MlSeoLinking = { InternalLinking };
})();
